'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "start", "end", "eventId", "occurrenceKey", "renderDragPreview", "isDraggable", "nativeButton"];
import * as React from 'react';
import { useStableCallback } from '@base-ui/utils/useStableCallback';
import { useStore } from '@base-ui/utils/store';
import { useButton, useRenderElement } from '@mui/x-scheduler-headless/base-ui-copy';
import { useDraggableEvent, generateOccurrenceFromEvent, useElementPositionInCollection } from '@mui/x-scheduler-headless/internals';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { schedulerEventSelectors } from '@mui/x-scheduler-headless/scheduler-selectors';
import { useEventTimelinePremiumStoreContext } from "../../use-event-timeline-premium-store-context/index.mjs";
import { useTimelineGridEventRowContext } from "../event-row/TimelineGridEventRowContext.mjs";
import { TimelineGridEventCssVars } from "./TimelineGridEventCssVars.mjs";
import { TimelineGridEventContext } from "./TimelineGridEventContext.mjs";
import { eventTimelinePremiumViewSelectors } from "../../event-timeline-premium-selectors/index.mjs";
import { TimelineGridEventDataAttributes } from "./TimelineGridEventDataAttributes.mjs";
import { jsxs as _jsxs } from "react/jsx-runtime";
const overflowStateAttributesMapping = {
  startingBeforeEdge: value => value ? {
    [TimelineGridEventDataAttributes.startingBeforeEdge]: ''
  } : null,
  endingAfterEdge: value => value ? {
    [TimelineGridEventDataAttributes.endingAfterEdge]: ''
  } : null
};
export const TimelineGridEvent = /*#__PURE__*/React.forwardRef(function TimelineGridEvent(componentProps, forwardedRef) {
  const {
      // Rendering props

      // Internal props
      start,
      end,
      eventId,
      occurrenceKey,
      renderDragPreview,
      isDraggable,
      nativeButton = false
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);

  // TODO: Expose a real `interactive` prop
  // to control whether the event should behave like a button
  const isInteractive = true;

  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventTimelinePremiumStoreContext();
  const {
    getCursorPositionInElementMs
  } = useTimelineGridEventRowContext();

  // Ref hooks
  const ref = React.useRef(null);

  // Selector hooks
  const viewConfig = useStore(store, eventTimelinePremiumViewSelectors.config);

  // Feature hooks
  const getSharedDragData = useStableCallback(input => {
    const offsetBeforeRowStart = Math.max(adapter.getTime(viewConfig.start) - start.timestamp, 0);
    const event = schedulerEventSelectors.processedEvent(store.state, eventId);
    const originalOccurrence = generateOccurrenceFromEvent({
      event,
      eventId,
      occurrenceKey,
      start,
      end
    });
    const offsetInsideRow = getCursorPositionInElementMs({
      input,
      elementRef: ref
    });
    return {
      eventId,
      occurrenceKey,
      originalOccurrence,
      start: start.value,
      end: end.value,
      initialCursorPositionInEventMs: offsetBeforeRowStart + offsetInsideRow
    };
  });
  const getDragData = useStableCallback(input => _extends({}, getSharedDragData(input), {
    source: 'TimelineGridEvent'
  }));
  const {
    state,
    preview,
    contextValue: draggableEventContextValue
  } = useDraggableEvent({
    ref,
    start,
    end,
    occurrenceKey,
    eventId,
    isDraggable,
    renderDragPreview,
    getDragData,
    collectionStart: viewConfig.start,
    collectionEnd: viewConfig.end
  });
  const {
    getButtonProps,
    buttonRef
  } = useButton({
    disabled: !isInteractive,
    native: nativeButton
  });
  const {
    position,
    duration,
    startingBeforeEdge,
    endingAfterEdge
  } = useElementPositionInCollection({
    start,
    end,
    collectionStart: viewConfig.start,
    collectionEnd: viewConfig.end
  });
  const mergedState = _extends({}, state, {
    startingBeforeEdge,
    endingAfterEdge
  });

  // Rendering hooks
  const style = React.useMemo(() => ({
    [TimelineGridEventCssVars.xPosition]: `${position * 100}%`,
    [TimelineGridEventCssVars.width]: `${duration * 100}%`
  }), [position, duration]);
  const props = {
    style
  };
  const contextValue = React.useMemo(() => _extends({}, draggableEventContextValue, {
    getSharedDragData
  }), [draggableEventContextValue, getSharedDragData]);
  const element = useRenderElement('div', componentProps, {
    state: mergedState,
    ref: [forwardedRef, ref, buttonRef],
    props: [props, elementProps, getButtonProps],
    stateAttributesMapping: overflowStateAttributesMapping
  });
  return /*#__PURE__*/_jsxs(TimelineGridEventContext.Provider, {
    value: contextValue,
    children: [element, preview.element]
  });
});
if (process.env.NODE_ENV !== "production") TimelineGridEvent.displayName = "TimelineGridEvent";